import { Box, Icon, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { TeacherPublicProfileQueryQuery } from "@outschool/gql-frontend-generated";
import { faCalendarAlt, faCalendarCheck } from "@outschool/icons";
import {
  getCountryOfResidence,
  useMachineTranslation,
  useTranslation
} from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { CollapsedText } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import { useTranslateCountryOfResidence } from "../../../../shared/hooks/useTranslateCountryOfResidence";
import HeaderTags from "../../HeaderTags";

interface ProfileSectionProps {
  children: React.ReactNode;
  fullWidth?: boolean;
  center?: boolean;
  hasBorder?: boolean;
}

const PAGE_MAX_WIDTH = 1120;

export const ProfileSection = React.forwardRef(
  (
    {
      children,
      fullWidth = false,
      center = true,
      hasBorder = true
    }: ProfileSectionProps,
    ref
  ) => (
    <Box
      ref={ref}
      sx={(theme: $TSFixMe) => ({
        maxWidth: fullWidth ? "none" : `${PAGE_MAX_WIDTH}px`,
        padding: fullWidth ? 0 : "0 24px",

        [theme.breakpoints.up("md")]: {
          padding: fullWidth ? 0 : "0 48px"
        },

        margin: center ? "0 auto" : "0"
      })}
    >
      <Box
        sx={{
          borderBottom: hasBorder ? "1px solid #DDDFE3 " : "none"
        }}
      >
        {children}
      </Box>
    </Box>
  )
);

export function TeacherMetaTags({
  leader,
  numberOfUpcomingListings,
  numberOfOtherListings,
  profileUrl
}: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherProfile\\TeacherProfileSummary"
  );
  if (!leader || !leader.name) {
    return null;
  }
  let reviewSummary = null;
  let upcomingSummary = null;
  if (numberOfUpcomingListings) {
    upcomingSummary = t("{{numberOfUpcomingListings}} upcoming class", {
      numberOfUpcomingListings,
      count: numberOfUpcomingListings,
      defaultValue_plural: "{{numberOfUpcomingListings}} upcoming classes"
    }) as any;
  }
  let otherSummary = null;
  if (numberOfOtherListings) {
    otherSummary = t("{{numberOfOtherListings}} class available by request", {
      numberOfOtherListings,
      count: numberOfOtherListings,
      defaultValue_plural:
        "{{numberOfOtherListings}} classes available by request"
    }) as any;
  }
  const title = `${leader.name} - ${
    leader.ownerOfSellerOrg ? t`Profile and Classes` : t`Outschool Teacher`
  }`;
  const description =
    leader.details.headline +
    " | " +
    _.compact([reviewSummary, upcomingSummary, otherSummary]).join(" | ");
  return (
    <HeaderTags
      title={title}
      meta={[
        {
          property: "og:type",
          content: "profile"
        },
        { name: "description", content: description },
        {
          property: "og:title",
          content: title
        },
        {
          property: "og:description",
          content: description
        },
        { property: "og:url", content: profileUrl },
        {
          property: "og:image",
          content: FilestackUrls.imageUrl(leader.photo, {
            w: 300,
            h: 300,
            fit: "crop"
          })
        },
        { property: "og:image:width", content: "300" },
        { property: "og:image:height", content: "300" },
        { name: "twitter:card", content: "summary_large_image" }
      ]}
      link={[{ rel: "canonical", href: profileUrl }]}
    />
  );
}
TeacherMetaTags.displayName = "TeacherMetaTags";

interface AboutProps {
  leader: NonNullable<
    NonNullable<TeacherPublicProfileQueryQuery["publicProfile"]>["leader"]
  >;
  userTimeZone: string;
}

export const About: React.FunctionComponent<AboutProps> = ({
  leader,
  userTimeZone
}: AboutProps) => {
  const isTablet = Screen.useIsTablet();
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherProfile\\TeacherProfileSummary"
  );
  const { translate } = useMachineTranslation();

  return (
    <Box
      sx={{
        width: isTablet ? "100%" : "50%"
      }}
    >
      <Box>
        <Typography variant="h3">
          {leader.ownerOfSellerOrg ? t`About us` : t`About me`}
        </Typography>
        {leader.details.about && (
          <CollapsedText maxCharacters={400}>
            {leader.details.about && translate(leader.details.about.trim())}
          </CollapsedText>
        )}
      </Box>
      <Summary leader={leader} userTimeZone={userTimeZone} />
    </Box>
  );
};

const Summary = ({ leader, userTimeZone }: $TSFixMe) => {
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherProfile\\TeacherProfileSummary"
  );
  const translateCountryOfResidence = useTranslateCountryOfResidence();
  const countryOfResidenceMeta = getCountryOfResidence(
    leader.details.countryOfResidence
  );

  const summary: { icon: JSX.Element; description: string }[] = [];

  if (leader.successfulClassCount) {
    summary.push({
      icon: (
        <Icon
          icon={faCalendarCheck}
          sx={{
            fontSize: "1.333em",
            color: "#5c6f8b"
          }}
        />
      ),
      description: t("{{numberOfSuccessfulClassCount}} class completed", {
        numberOfSuccessfulClassCount: leader.successfulClassCount,
        count: leader.successfulClassCount,
        defaultValue_plural:
          "{{numberOfSuccessfulClassCount}} classes completed"
      })
    });
  }

  if (countryOfResidenceMeta) {
    summary.push({
      icon: (
        <Box
          sx={{
            fontSize: "2.5rem"
          }}
        >
          {countryOfResidenceMeta.icon}
        </Box>
      ),
      description: t("Lives in {{countryOfResidence}}", {
        countryOfResidence: translateCountryOfResidence(
          countryOfResidenceMeta.code
        )
      })
    });
  }

  summary.push({
    icon: (
      <Icon
        icon={faCalendarAlt}
        sx={{
          fontSize: "1.333em",
          color: "#5c6f8b"
        }}
      />
    ),
    description: t("Joined {{leaderApprovedAtDate}}", {
      leaderApprovedAtDate: dayjs
        .tz(leader.teacher_approved_at, userTimeZone)
        .format("ll")
    })
  });

  return (
    <Box flex>
      <Box
        flex
        sx={{
          flexDirection: "column"
        }}
      >
        {summary.map(({ icon, description }, index) => (
          <Box
            flex
            key={index}
            sx={{
              alignItems: "center",
              height: "2rem",
              marginY: "0.5em"
            }}
          >
            <Box
              sx={{
                width: "3rem",
                marginRight: "0.5em",
                textAlign: "center"
              }}
            >
              {icon}
            </Box>
            {description}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
